/**
 * charm-builder.js
 * ============================================================================
 * Charm Builder — Product Page Interactive Module
 *
 * Responsibilities:
 *  - Parse charm product data from the page (injected by Liquid as JSON)
 *  - Handle charm selection / deselection / max-limit enforcement
 *  - Sync carousel cards and popup grid cards bidirectionally
 *  - Update price display in real time
 *  - Manage the "View All" popup (open/close, focus trap, ESC, backdrop click)
 *  - Variant Picker Popup: for multi-variant charms, open a chip-based picker
 *    showing ONLY the "Letter" option axis (A–Z). Single-variant products use
 *    the existing direct-select flow unchanged.
 *  - Multi-select: multiple letter variants of the same charm each count as a
 *    separate selected item (their letter is shown in the carousel track).
 *  - Intercept the product form submit and perform an AJAX multi-item add-to-cart
 *  - Fire cart update events compatible with Rebuy Smart Cart and theme cart.js
 * ============================================================================
 */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Money Formatting Utility
  -------------------------------------------------------------------------- */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    const amount   = (cents / 100).toFixed(2);
    const amountND = Math.round(cents / 100).toString();

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        const fmt = window.Shopify.money_format || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) { /* fall through */ }
    }

    const fmt = (window.theme && window.theme.moneyFormat)
      || (window.Shopify && window.Shopify.money_format)
      || '${{amount}}';

    return fmt
      .replace('{{amount_no_decimals_with_comma_separator}}', amountND.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_with_comma_separator}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountND)
      .replace('{{ amount_no_decimals }}', amountND)
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount);
  }

  /* --------------------------------------------------------------------------
     CharmBuilder Class
  -------------------------------------------------------------------------- */
  class CharmBuilder {
    constructor(el) {
      if (!el || el.dataset.charmBuilderInit) return;
      el.dataset.charmBuilderInit = '1';

      this.el         = el;
      this.sectionId  = el.dataset.sectionId || '';
      this.maxCharms  = parseInt(el.dataset.maxCharms, 10) || 5;
      this.maxMessage = el.dataset.maxMessage || `You can select up to ${this.maxCharms} charms.`;
      this.basePrice  = parseInt(el.dataset.basePrice, 10) || 0;
      this.formId     = el.dataset.productFormId || '';

      /*
       * State:
       *   Map<variantId(string), {
       *     variantId:    string,   — actual Shopify variant ID
       *     productId:    string,   — parent product ID
       *     price:        number,   — price in cents
       *     title:        string,   — full display title e.g. "Initial Letter Charm — A"
       *     displayLabel: string,   — short label shown on carousel card e.g. "A"
       *   }>
       */
      this._selected = new Map();

      this._charms = this._parseCharmData();

      /* Main popup DOM refs */
      this._carousel      = el.querySelector('.charm-builder__carousel');
      this._track         = el.querySelector('[data-charm-track]');
      this._popupEl       = el.querySelector('.charm-builder__popup');
      this._backdropEl    = el.querySelector('.charm-builder__popup-backdrop');
      this._countEl       = el.querySelector('[data-charm-count]');
      this._priceDeltaEl  = el.querySelector('[data-charm-price-delta]');
      this._metaBadgeEl   = el.querySelector('[data-charm-meta-badge]');
      this._limitMsgEl    = el.querySelector('[data-charm-limit-msg]');
      this._viewAllBtn    = el.querySelector('[data-charm-view-all]');
      this._popupDoneBtn  = el.querySelector('[data-charm-popup-done]');
      this._popupCloseBtn = el.querySelector('[data-charm-popup-close]');

      /* Variant popup DOM refs */
      this._variantPopupEl       = el.querySelector('[data-charm-variant-popup]');
      this._variantBackdropEl    = el.querySelector('[data-charm-variant-backdrop]');
      this._variantGridEl        = el.querySelector('[data-charm-variant-grid]');
      this._variantPopupTitleEl  = el.querySelector('[data-charm-variant-popup-title]');
      this._variantOptionLabelEl = el.querySelector('[data-charm-variant-option-label]');
      this._variantCloseBtn      = el.querySelector('[data-charm-variant-close]');
      this._variantAddBtn        = el.querySelector('[data-charm-variant-add]');

      this._lastFocus           = null;
      this._variantProductId    = null;    // product being shown in variant popup
      this._variantTempSelected = new Set(); // Set<variantId> temp selections in variant popup

      // Capture the card IDs initially rendered in the track
      this._initialTrackCardIds = [];
      if (this._track) {
        this._initialTrackCardIds = Array.from(this._track.querySelectorAll('[data-charm-id]'))
          .map((c) => c.dataset.charmId);
      }

      // Read responsive initial counts from Liquid data-hide-* attributes.
      // These tell us how many charm cards (excluding None) to show per screen size.
      // desktop_initial_count = cards where data-hide-desktop != 'true'
      // mobile_initial_count  = cards where data-hide-mobile  != 'true'
      if (this._track) {
        const nonNoneCards = Array.from(
          this._track.querySelectorAll('[data-charm-id]:not([data-charm-id="none"])')
        );
        this._desktopInitialCount = nonNoneCards.filter(
          (c) => c.dataset.hideDesktop !== 'true'
        ).length;
        this._mobileInitialCount = nonNoneCards.filter(
          (c) => c.dataset.hideMobile !== 'true'
        ).length;
        // Fallback if Liquid attributes not found (default 4 desktop, 6 mobile)
        if (this._desktopInitialCount === 0) this._desktopInitialCount = 4;
        if (this._mobileInitialCount  === 0) this._mobileInitialCount  = 6;
      } else {
        this._desktopInitialCount = 4;
        this._mobileInitialCount  = 6;
      }

      this._bindEvents();
      this._interceptFormSubmit();
      this._updateUI();
    }

    /* -----------------------------------------------------------------------
       Data Parsing
    ----------------------------------------------------------------------- */
    _parseCharmData() {
      const dataEl = this.el.querySelector('[data-charm-data]');
      if (!dataEl) return [];
      try {
        const raw = JSON.parse(dataEl.textContent) || [];
        return raw.map((c) => ({
          ...c,
          id:         String(c.id),
          variant_id: String(c.variant_id),
          options:    c.options || [],
          variants:   (c.variants || []).map((v) => ({
            ...v,
            id:      String(v.id),
            option1: v.option1 || null,
            option2: v.option2 || null,
            option3: v.option3 || null,
          })),
        }));
      } catch (e) {
        console.warn('[CharmBuilder] Could not parse charm data:', e);
        return [];
      }
    }

    /* -----------------------------------------------------------------------
       Event Binding
    ----------------------------------------------------------------------- */
    _bindEvents() {
      // Carousel card clicks
      if (this._track) {
        this._track.addEventListener('click', (e) => this._onCardClick(e));
        this._track.addEventListener('keydown', (e) => this._onCardKeydown(e));
      }

      // Popup grid card clicks
      if (this._popupEl) {
        const popupGrid = this._popupEl.querySelector('[data-charm-popup-grid]');
        if (popupGrid) {
          popupGrid.addEventListener('click', (e) => this._onCardClick(e));
          popupGrid.addEventListener('keydown', (e) => this._onCardKeydown(e));
        }
      }

      // View All → open popup
      if (this._viewAllBtn) {
        this._viewAllBtn.addEventListener('click', () => this._openPopup());
      }

      // Main popup buttons
      if (this._popupCloseBtn) {
        this._popupCloseBtn.addEventListener('click', () => this._closePopup());
      }
      if (this._popupDoneBtn) {
        this._popupDoneBtn.addEventListener('click', () => {
          this._commitPopupSelection();
          this._closePopup();
        });
      }
      if (this._backdropEl) {
        this._backdropEl.addEventListener('click', () => this._closePopup());
      }

      // Variant popup buttons
      if (this._variantCloseBtn) {
        this._variantCloseBtn.addEventListener('click', () => this._closeVariantPopup(false));
      }
      if (this._variantAddBtn) {
        this._variantAddBtn.addEventListener('click', () => this._commitVariantSelection());
      }
      if (this._variantBackdropEl) {
        this._variantBackdropEl.addEventListener('click', () => this._closeVariantPopup(false));
      }
      if (this._variantGridEl) {
        this._variantGridEl.addEventListener('click', (e) => this._onVariantChipClick(e));
        this._variantGridEl.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            const chip = e.target.closest('[data-variant-id]');
            if (chip) { e.preventDefault(); chip.click(); }
          }
        });
      }

      // Global ESC handler
      this._onKeydown = this._handleKeydown.bind(this);
      document.addEventListener('keydown', this._onKeydown);
    }

    /* -----------------------------------------------------------------------
       Card Click / Keyboard Handlers
    ----------------------------------------------------------------------- */
    _onCardClick(e) {
      const card = e.target.closest('[data-charm-id]');
      if (!card) return;
      e.preventDefault();

      const charmId    = card.dataset.charmId;
      const hasVariants = card.dataset.hasVariants === 'true';
      const productId  = card.dataset.charmProductId;
      const isPopup    = !!card.closest('.charm-builder__popup');

      // "None" card — clear all without variant popup
      if (charmId === 'none') {
        if (isPopup) {
          if (!this._tempSelected) return;
          this._tempSelected.clear();
          this._updatePopupUI();
        } else {
          this._selected.clear();
          this._updateUI();
        }
        return;
      }

      if (card.getAttribute('aria-disabled') === 'true') return;

      // Clicked from outside section:
      // - Variant product → open main Charm Popup so user can pick a variant
      // - Non-variant product → directly toggle selection
      if (!isPopup) {
        if (hasVariants) {
          this._openPopup();
          return;
        }
        // Direct toggle for non-variant outside cards
        this._toggleCharmState(charmId, productId, this._selected);
        this._updateUI();
        return;
      }

      // Clicked inside Charm Popup — variant product:
      // If already selected → deselect it directly (no picker needed).
      // If not yet selected → open variant picker to choose a variant.
      if (hasVariants && productId) {
        if (!this._tempSelected) return;
        const alreadySelected = this._isProductSelected(productId, this._tempSelected);
        if (alreadySelected) {
          // Remove all variants of this product from temp selection
          const toRemove = [];
          this._tempSelected.forEach((info, variantId) => {
            if (String(info.productId) === String(productId)) toRemove.push(variantId);
          });
          toRemove.forEach((id) => this._tempSelected.delete(id));
          this._updatePopupUI();
          return;
        }
        this._openVariantPopup(productId);
        return;
      }

      // Single-variant product → direct toggle inside popup
      if (!this._tempSelected) return;
      this._toggleCharmState(charmId, productId, this._tempSelected);
      this._updatePopupUI();
    }

    _onCardKeydown(e) {
      if (e.key === ' ' || e.key === 'Enter') {
        const card = e.target.closest('[data-charm-id]');
        if (card) { e.preventDefault(); card.click(); }
      }
    }

    /* -----------------------------------------------------------------------
       Toggle Charm Selection State (single-variant products)
    ----------------------------------------------------------------------- */
    _toggleCharmState(variantId, productId, map) {
      if (map.has(variantId)) {
        map.delete(variantId);
        return;
      }

      if (map.size >= this.maxCharms) return;

      const info = this._findVariantInfo(variantId, productId);
      if (!info) return;

      map.set(variantId, info);
    }

    /**
     * Build the selection info object for a variant ID.
     * Searches both top-level product list (single-variant) and nested variants.
     */
    _findVariantInfo(variantId, productId) {
      // Search top-level (single-variant products use variant_id as identifier)
      for (const charm of this._charms) {
        if (charm.variant_id === variantId) {
          return {
            variantId,
            productId:    charm.id,
            price:        charm.price,
            title:        charm.title,
            displayLabel: charm.title,
          };
        }

        // Search nested variants (multi-variant products)
        if (charm.variants) {
          const v = charm.variants.find((v) => v.id === variantId);
          if (v) {
            return {
              variantId,
              productId:    charm.id,
              price:        v.price,
              title:        charm.title,
              displayLabel: charm.title,
            };
          }
        }
      }
      return null;
    }

    _commitPopupSelection() {
      if (this._tempSelected) {
        // _tempSelected may already contain variant selections committed while
        // the View-All popup was open. Copy the whole map into _selected so
        // the carousel track reflects all picks (both direct and variant).
        this._selected = new Map(this._tempSelected);
        this._updateUI(); // syncs carousel + badges
      }
    }

    _updatePopupUI() {
      if (!this._popupEl || !this._tempSelected) return;

      const count = this._tempSelected.size;
      const atMax = count >= this.maxCharms;

      this._popupEl.querySelectorAll('[data-charm-id]').forEach((card) => {
        const charmId    = card.dataset.charmId;
        const isNone     = charmId === 'none';
        const hasVariants = card.dataset.hasVariants === 'true';
        const productId  = card.dataset.charmProductId;

        if (isNone) {
          card.setAttribute('aria-pressed', String(count === 0));
          return;
        }

        // Multi-variant card: highlight if any variant of this product is in _tempSelected
        if (hasVariants && productId) {
          const anySelected = this._isProductSelected(productId, this._tempSelected);
          card.setAttribute('aria-pressed', String(anySelected));
          // At max: disable if no variant of this product has been picked yet
          if (atMax && !anySelected) {
            card.setAttribute('aria-disabled', 'true');
          } else {
            card.removeAttribute('aria-disabled');
          }
          return;
        }

        const isSelected = this._tempSelected.has(charmId);
        card.setAttribute('aria-pressed', String(isSelected));

        if (atMax && !isSelected) {
          card.setAttribute('aria-disabled', 'true');
        } else {
          card.removeAttribute('aria-disabled');
        }
      });
    }

    /* -----------------------------------------------------------------------
       UI Sync — call after every state change
    ----------------------------------------------------------------------- */
    _updateUI() {
      const count         = this._selected.size;
      const atMax         = count >= this.maxCharms;
      const hasSelections = count > 0;

      this._syncMainTrack();

      // Sync all card aria-pressed states
      this.el.querySelectorAll('[data-charm-id]').forEach((card) => {
        const charmId    = card.dataset.charmId;
        const isNone     = charmId === 'none';
        const hasVariants = card.dataset.hasVariants === 'true';
        const productId  = card.dataset.charmProductId;

        if (isNone) {
          card.setAttribute('aria-pressed', String(!hasSelections));
          card.removeAttribute('aria-disabled');
          return;
        }

        // Multi-variant product card (e.g. in View-All popup):
        // show as selected if ANY variant of this product is selected.
        if (hasVariants && productId) {
          const anySelected = this._isProductSelected(productId, this._selected);
          card.setAttribute('aria-pressed', String(anySelected));
          // At max: disable if no variant of this product has been picked yet.
          // Allow re-opening the picker when already selected (to change / add more).
          if (atMax && !anySelected) {
            card.setAttribute('aria-disabled', 'true');
          } else {
            card.removeAttribute('aria-disabled');
          }
          return;
        }

        const isSelected = this._selected.has(charmId);
        card.setAttribute('aria-pressed', String(isSelected));

        if (atMax && !isSelected) {
          card.setAttribute('aria-disabled', 'true');
        } else {
          card.removeAttribute('aria-disabled');
        }

        // Update label/price for single-variant cards from JSON data
        const charm = this._charms.find((c) => c.variant_id === charmId);
        if (charm) {
          const labelEl = card.querySelector('.charm-builder__card-label');
          const priceEl = card.querySelector('.charm-builder__card-price');
          if (labelEl) {
            const title = charm.title || '';
            labelEl.textContent = title.length > 14 ? title.slice(0, 13) + '\u2026' : title;
          }
          if (priceEl) {
            priceEl.textContent = '+' + formatMoney(charm.price);
          }
          card.setAttribute('aria-label', `${charm.title} \u2014 ${formatMoney(charm.price)}`);
        }
      });

      // Meta badge
      if (this._metaBadgeEl) {
        if (hasSelections) this._metaBadgeEl.removeAttribute('hidden');
        else               this._metaBadgeEl.setAttribute('hidden', '');
      }
      if (this._countEl) {
        this._countEl.textContent = count + ' item' + (count !== 1 ? 's' : '');
      }
      if (this._priceDeltaEl) {
        this._priceDeltaEl.textContent = '+' + formatMoney(this._getTotalCharmPrice());
      }

      // Limit message
      if (this._limitMsgEl) {
        if (atMax) this._limitMsgEl.removeAttribute('hidden');
        else       this._limitMsgEl.setAttribute('hidden', '');
      }

      this._announceToScreenReader(
        hasSelections
          ? `${count} charm${count !== 1 ? 's' : ''} selected.`
          : 'No charms selected.'
      );
    }

    /* -----------------------------------------------------------------------
       PDP Grid Sync
       - Single-variant products use their Liquid-rendered card (already in track)
       - Multi-variant selections use synthetic cards (built in JS) showing the letter
    ----------------------------------------------------------------------- */
    _syncMainTrack() {
      if (!this._track) return;

      const selectedIds        = Array.from(this._selected.keys());
      const dynamicSelectedIds = selectedIds.filter(
        (id) => !this._initialTrackCardIds.includes(id)
      );

      // Ensure card elements exist for all dynamic (variant-selection) selected IDs
      dynamicSelectedIds.forEach((id) => {
        if (!this._track.querySelector(`[data-charm-id="${id}"]`)) {
          const info = this._selected.get(id);
          if (info) {
            const card = this._buildSyntheticCard(id, info);
            if (card) this._track.appendChild(card);
          }
        }
      });

      // Product IDs that already have a synthetic selection card —
      // used to suppress the Liquid placeholder duplicate.
      const dynamicProductIds = new Set();
      dynamicSelectedIds.forEach((id) => {
        const info = this._selected.get(id);
        if (info && info.productId) dynamicProductIds.add(String(info.productId));
      });

      // Determine the cap: how many charm cards (excl. None) to show based on screen size.
      const isMobile   = window.matchMedia('(max-width: 640px)').matches;
      const charmSlots = isMobile ? this._mobileInitialCount : this._desktopInitialCount;

      // Gather all selected charm cards (initial + synthetic), in Liquid sequence order.
      // Selected initial cards (non-variant or variant placeholder whose product is selected).
      const selectedCards   = [];
      const unselectedCards = [];

      this._initialTrackCardIds.forEach((id) => {
        if (id === 'none') return;
        const card = this._track.querySelector(`[data-charm-id="${id}"]`);
        if (!card) return;

        // Skip Liquid placeholder if its product has a synthetic card
        const cardHasVariants = card.dataset.hasVariants === 'true';
        const cardProductId   = card.dataset.charmProductId;
        if (cardHasVariants && cardProductId && dynamicProductIds.has(String(cardProductId))) return;

        if (this._selected.has(id)) {
          selectedCards.push(card);
        } else {
          unselectedCards.push(card);
        }
      });

      // Add synthetic (dynamic) selected cards after Liquid-based ones
      dynamicSelectedIds.forEach((id) => {
        const card = this._track.querySelector(`[data-charm-id="${id}"]`);
        if (card && !selectedCards.includes(card)) selectedCards.push(card);
      });

      // Fill remaining slots with unselected initial cards up to the cap.
      // This ensures total charm cards shown = charmSlots (no extra rows).
      const remainingSlots = Math.max(0, charmSlots - selectedCards.length);
      const fillerCards    = unselectedCards.slice(0, remainingSlots);

      // Final order: [None] → [selected] → [unselected fillers]
      const noneCard    = this._track.querySelector('[data-charm-id="none"]');
      const visibleCards = [];
      if (noneCard) visibleCards.push(noneCard);
      selectedCards.forEach((c) => visibleCards.push(c));
      fillerCards.forEach((c)   => visibleCards.push(c));

      // Hide all track cards first
      Array.from(this._track.querySelectorAll('[data-charm-id]')).forEach((c) => {
        c.style.display = 'none';
        c.setAttribute('hidden', '');
      });

      // Re-append and show only visible cards in the new order
      visibleCards.forEach((card) => {
        if (card) {
          card.style.display = '';
          card.removeAttribute('hidden');
          this._track.appendChild(card);
        }
      });
    }

    /**
     * Build a synthetic card for a variant-selected charm (e.g. letter "A").
     * The card shows:
     *  - Product image (from JSON charm data)
     *  - displayLabel as the card label (e.g. "A")
     *  - Price
     * Clicking the card deselects it (via normal event delegation in _onCardClick).
     */
    _buildSyntheticCard(variantId, info) {
      const charm = this._charms.find((c) => c.id === String(info.productId));

      const btn = document.createElement('button');
      btn.className = 'charm-builder__card';
      btn.type = 'button';
      btn.dataset.charmId = variantId;
      btn.dataset.charmProductId = String(info.productId);
      btn.dataset.hasVariants = 'false'; // synthetic cards deselect directly on click
      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('aria-label', `${info.title} \u2014 ${formatMoney(info.price)}`);

      const imgWrap = document.createElement('span');
      imgWrap.className = 'charm-builder__card-img-wrap';
      imgWrap.setAttribute('aria-hidden', 'true');

      if (charm && charm.image) {
        const img = document.createElement('img');
        img.src = charm.image;
        img.alt = info.displayLabel || info.title;
        img.loading = 'lazy';
        imgWrap.appendChild(img);
      }

      // Main label: product title (truncated)
      const label = document.createElement('span');
      label.className = 'charm-builder__card-label';
      label.setAttribute('aria-hidden', 'true');
      const productTitle = (charm && charm.title) || info.title || '';
      const truncatedTitle = productTitle.length > 14 ? productTitle.slice(0, 13) + '\u2026' : productTitle;
      label.textContent = truncatedTitle;

      btn.appendChild(imgWrap);
      btn.appendChild(label);

      // Sub-label: selected variant / letter (only when different from product title)
      if (info.displayLabel && info.displayLabel !== productTitle) {
        const varLabel = document.createElement('span');
        varLabel.className = 'charm-builder__card-variant-label';
        varLabel.setAttribute('aria-hidden', 'true');
        varLabel.textContent = info.displayLabel;
        btn.appendChild(varLabel);
      }

      const priceEl = document.createElement('span');
      priceEl.className = 'charm-builder__card-price';
      priceEl.setAttribute('aria-hidden', 'true');
      priceEl.textContent = '+' + formatMoney(info.price);

      btn.appendChild(priceEl);

      return btn;
    }

    _getTotalCharmPrice() {
      let total = 0;
      this._selected.forEach((c) => { total += c.price; });
      return total;
    }

    /* -----------------------------------------------------------------------
       Screen Reader Live Region
    ----------------------------------------------------------------------- */
    _announceToScreenReader(message) {
      let region = this.el.querySelector('[data-charm-live-region]');
      if (!region) {
        region = document.createElement('span');
        region.setAttribute('data-charm-live-region', '');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        region.className = 'visually-hidden';
        this.el.appendChild(region);
      }
      region.textContent = message;
    }

    /* -----------------------------------------------------------------------
       View-All Popup — Open / Close
    ----------------------------------------------------------------------- */
    _openPopup() {
      if (!this._popupEl || !this._backdropEl) return;

      this._tempSelected = new Map(this._selected);
      this._updatePopupUI();

      this._lastFocus = document.activeElement;

      this._backdropEl.classList.add('is-open');
      this._popupEl.classList.add('is-open');
      this._popupEl.removeAttribute('aria-hidden');

      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        if (this._popupCloseBtn) this._popupCloseBtn.focus();
      });
    }

    _closePopup() {
      if (!this._popupEl || !this._backdropEl) return;

      this._tempSelected = null;

      this._backdropEl.classList.remove('is-open');
      this._popupEl.classList.remove('is-open');
      this._popupEl.setAttribute('aria-hidden', 'true');

      document.body.style.overflow = '';

      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        this._lastFocus.focus();
        this._lastFocus = null;
      }
    }

    /* -----------------------------------------------------------------------
       Variant Picker Popup — Open
       Finds the "Letter" option (or equivalent) and renders only those chips.
       Pre-selects any variants for this product already in this._selected.
    ----------------------------------------------------------------------- */
    _openVariantPopup(productId) {
      if (!this._variantPopupEl || !this._variantBackdropEl) return;

      const charm = this._charms.find((c) => c.id === String(productId));
      if (!charm || !charm.variants || charm.variants.length === 0) return;

      this._variantProductId    = String(productId);
      this._variantTempSelected = new Set();

      // Pre-populate: any variants for this product already committed
      // Check both _selected and _tempSelected (in case View-All is open)
      const activeMap = (this._tempSelected !== null) ? this._tempSelected : this._selected;
      activeMap.forEach((info, variantId) => {
        if (String(info.productId) === this._variantProductId) {
          this._variantTempSelected.add(variantId);
        }
      });

      // Title = product name only (no "Select X for" prefix)
      if (this._variantPopupTitleEl) {
        this._variantPopupTitleEl.textContent = charm.title || 'Select a charm';
      }

      // Option sub-label above chips: "Select letter:" (lowercase)
      if (this._variantOptionLabelEl) {
        const optName = this._getTargetOptionName(charm);
        this._variantOptionLabelEl.textContent = `Select ${optName.toLowerCase()}:`;
      }

      // Build chips — show only Letter option values, deduplicated
      this._buildVariantChips(charm);

      this._updateVariantAddBtn();

      this._lastFocus = document.activeElement;

      this._variantBackdropEl.classList.add('is-open');
      this._variantPopupEl.classList.add('is-open');
      this._variantPopupEl.removeAttribute('aria-hidden');

      // Lock scroll only if the View-All popup is not already locking it
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        if (this._variantCloseBtn) this._variantCloseBtn.focus();
      });
    }

    /**
     * Determine the option name to display in the picker.
     * Prefers "Letter", then any non-"Material"/"Color"/"Size" option,
     * then falls back to the last option name.
     */
    _getTargetOptionName(charm) {
      const options = charm.options || [];
      const skipWords = ['material', 'color', 'colour', 'size', 'finish', 'metal'];

      let idx = options.findIndex((o) => o.toLowerCase() === 'letter');
      if (idx === -1) {
        idx = options.findIndex((o) => o.toLowerCase().includes('letter'));
      }
      if (idx === -1) {
        idx = options.findIndex((o) => !skipWords.some((w) => o.toLowerCase().includes(w)));
      }
      if (idx === -1) idx = options.length - 1;

      return options[idx] || 'variant';
    }

    /**
     * Get the option key (option1 / option2 / option3) that corresponds to
     * the "Letter" (or equivalent) option for the given charm product.
     */
    _getTargetOptionKey(charm) {
      const options = charm.options || [];
      const skipWords = ['material', 'color', 'colour', 'size', 'finish', 'metal'];

      let idx = options.findIndex((o) => o.toLowerCase() === 'letter');
      if (idx === -1) {
        idx = options.findIndex((o) => o.toLowerCase().includes('letter'));
      }
      if (idx === -1) {
        idx = options.findIndex((o) => !skipWords.some((w) => o.toLowerCase().includes(w)));
      }
      if (idx === -1) idx = options.length - 1;

      return idx >= 0 ? `option${idx + 1}` : 'option1';
    }

    /**
     * Build variant chip buttons in the variant popup grid.
     * Shows ONLY the target option axis (Letter A–Z), deduplicating by value.
     * Maps each chip to the first available variant with that option value.
     */
    _buildVariantChips(charm) {
      this._variantGridEl.innerHTML = '';

      const optionKey = this._getTargetOptionKey(charm);
      const seen      = new Set();

      charm.variants.forEach((v) => {
        const displayValue = v[optionKey] || v.title;

        // Deduplicate by letter value (skip duplicate Material rows)
        if (seen.has(displayValue)) return;
        seen.add(displayValue);

        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'charm-builder__variant-chip';
        chip.dataset.variantId    = v.id;
        chip.dataset.displayValue = displayValue;
        chip.textContent = displayValue;
        chip.setAttribute('aria-pressed', String(this._variantTempSelected.has(v.id)));

        if (!v.available) {
          chip.disabled = true;
          chip.setAttribute('aria-disabled', 'true');
          chip.title = 'Unavailable';
        }

        this._variantGridEl.appendChild(chip);
      });
    }

    /* -----------------------------------------------------------------------
       Variant Picker Popup — Close (without committing)
       keepBodyLock: if true, don't restore overflow (View-All popup still open)
    ----------------------------------------------------------------------- */
    _closeVariantPopup(keepBodyLock) {
      if (!this._variantPopupEl || !this._variantBackdropEl) return;

      this._variantProductId    = null;
      this._variantTempSelected = new Set();

      this._variantBackdropEl.classList.remove('is-open');
      this._variantPopupEl.classList.remove('is-open');
      this._variantPopupEl.setAttribute('aria-hidden', 'true');

      // Only unlock body scroll when the View-All popup is also closed
      if (!keepBodyLock && (!this._popupEl || !this._popupEl.classList.contains('is-open'))) {
        document.body.style.overflow = '';
      }

      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        this._lastFocus.focus();
        this._lastFocus = null;
      }
    }

    /* -----------------------------------------------------------------------
       Variant Chip Click Handler
    ----------------------------------------------------------------------- */
    _onVariantChipClick(e) {
      const chip = e.target.closest('[data-variant-id]');
      if (!chip || chip.disabled) return;

      const variantId = chip.dataset.variantId;

      if (this._variantTempSelected.has(variantId)) {
        this._variantTempSelected.delete(variantId);
        chip.setAttribute('aria-pressed', 'false');
      } else {
        // Check against global max using the active map:
        // use _tempSelected if View-All popup is open (so temp picks count too)
        const viewAllOpen = !!(this._popupEl && this._popupEl.classList.contains('is-open'));
        const baseMap = (viewAllOpen && this._tempSelected !== null)
          ? this._tempSelected
          : this._selected;
        const existingForProduct = this._countProductSelectionsIn(this._variantProductId, baseMap);
        const projectedTotal =
          (baseMap.size - existingForProduct)
          + this._variantTempSelected.size
          + 1;

        if (projectedTotal > this.maxCharms) {
          this._announceToScreenReader(this.maxMessage);
          return;
        }

        this._variantTempSelected.add(variantId);
        chip.setAttribute('aria-pressed', 'true');
      }

      this._updateVariantAddBtn();
    }

    _countProductSelectionsInMain(productId) {
      return this._countProductSelectionsIn(productId, this._selected);
    }

    /**
     * Count selections for a given productId in any Map<variantId, info>.
     * Works for both this._selected and this._tempSelected.
     */
    _countProductSelectionsIn(productId, map) {
      let n = 0;
      map.forEach((info) => {
        if (String(info.productId) === String(productId)) n++;
      });
      return n;
    }

    /**
     * Returns true if any entry in the given Map has productId as its productId.
     * Works with both this._selected and this._tempSelected (both are Maps after the
     * latest refactor; _tempSelected = new Map(this._selected) copy).
     * @param {string} productId
     * @param {Map} map
     */
    _isProductSelected(productId, map) {
      for (const [, info] of map) {
        if (String(info.productId) === String(productId)) return true;
      }
      return false;
    }

    _updateVariantAddBtn() {
      if (!this._variantAddBtn) return;
      const n = this._variantTempSelected.size;
      this._variantAddBtn.textContent = n > 0
        ? `Add Selected (${n})`
        : 'Add Selected';
    }

    /* -----------------------------------------------------------------------
       Commit Variant Selection — called when "Add Selected" is pressed.
       Replaces previous selections for this product, adds new letter variants.
       Stores displayLabel = the letter character (e.g. "A") so the carousel
       card clearly shows which letter is selected.
    ----------------------------------------------------------------------- */
    _commitVariantSelection() {
      if (!this._variantProductId) {
        this._closeVariantPopup(false);
        return;
      }

      const charm = this._charms.find((c) => c.id === String(this._variantProductId));
      if (!charm) {
        this._closeVariantPopup(false);
        return;
      }

      const viewAllOpen = !!(this._popupEl && this._popupEl.classList.contains('is-open'));
      const optionKey   = this._getTargetOptionKey(charm);

      if (viewAllOpen && this._tempSelected !== null) {
        /*
         * View-All popup is open — commit into _tempSelected only.
         * The main carousel DOES NOT update yet; it updates when the
         * customer clicks "Done" in the View-All popup (_commitPopupSelection).
         */
        // Remove previous selections for this product from _tempSelected
        const toRemoveTemp = [];
        this._tempSelected.forEach((info, variantId) => {
          if (String(info.productId) === this._variantProductId) toRemoveTemp.push(variantId);
        });
        toRemoveTemp.forEach((id) => this._tempSelected.delete(id));

        // Add newly chosen variants into _tempSelected
        this._variantTempSelected.forEach((variantId) => {
          if (this._tempSelected.size >= this.maxCharms) return;
          const v = charm.variants.find((v) => v.id === variantId);
          if (!v) return;
          const displayLabel = (v[optionKey] || v.title || '').toString();
          this._tempSelected.set(variantId, {
            variantId,
            productId:    charm.id,
            price:        v.price,
            title:        `${charm.title} — ${displayLabel}`,
            displayLabel: displayLabel,
          });
        });

        this._closeVariantPopup(true); // keep body locked — View-All still open
        this._updatePopupUI();         // highlight the product card in View-All popup

      } else {
        /*
         * No View-All popup — direct selection from the main carousel.
         * Commit immediately into _selected and update the carousel.
         */
        const toRemove = [];
        this._selected.forEach((info, variantId) => {
          if (String(info.productId) === this._variantProductId) toRemove.push(variantId);
        });
        toRemove.forEach((id) => {
          this._selected.delete(id);
          if (!this._initialTrackCardIds.includes(id) && this._track) {
            const old = this._track.querySelector(`[data-charm-id="${id}"]`);
            if (old) old.remove();
          }
        });

        this._variantTempSelected.forEach((variantId) => {
          if (this._selected.size >= this.maxCharms) return;
          const v = charm.variants.find((v) => v.id === variantId);
          if (!v) return;
          const displayLabel = (v[optionKey] || v.title || '').toString();
          this._selected.set(variantId, {
            variantId,
            productId:    charm.id,
            price:        v.price,
            title:        `${charm.title} — ${displayLabel}`,
            displayLabel: displayLabel,
          });
        });

        this._closeVariantPopup(false);
        this._updateUI(); // updates carousel immediately
      }
    }

    /* -----------------------------------------------------------------------
       Keyboard Handler — ESC + Focus Trap
    ----------------------------------------------------------------------- */
    _handleKeydown(e) {
      // Variant popup takes priority
      if (this._variantPopupEl && this._variantPopupEl.classList.contains('is-open')) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this._closeVariantPopup(false);
          return;
        }
        if (e.key === 'Tab') this._trapFocus(e, this._variantPopupEl);
        return;
      }

      // Main popup
      if (!this._popupEl || !this._popupEl.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this._closePopup();
        return;
      }
      if (e.key === 'Tab') this._trapFocus(e, this._popupEl);
    }

    _trapFocus(e, container) {
      const focusable = Array.from(
        container.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    /* -----------------------------------------------------------------------
       AJAX Add-to-Cart — Intercept Form Submit
    ----------------------------------------------------------------------- */
    _interceptFormSubmit() {
      if (!this.formId) return;
      const form = document.getElementById(this.formId);
      if (!form) return;

      form.addEventListener('submit', (e) => {
        const hasInvalid = focusFirstInvalidRequiredField(form);
        if (hasInvalid) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        if (this._selected.size === 0) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this._addToCart(form);
      }, { capture: true });
    }

    async _addToCart(form) {
      const submitBtn = form.querySelector('[data-add-to-cart]') ||
                        form.querySelector('[type="submit"]');

      if (submitBtn) {
        submitBtn.classList.add('btn--loading');
        submitBtn.setAttribute('disabled', '');
      }

      const baseVariantId = form.querySelector('input[name="id"], select[name="id"]')?.value;
      if (!baseVariantId) {
        console.warn('[CharmBuilder] Could not find base variant ID in form.');
        this._restoreSubmitBtn(submitBtn);
        return;
      }

      const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1;

      const properties = {};
      try {
        const fd = new FormData(form);
        for (const [name, value] of fd.entries()) {
          const m = name.match(/^properties\[([^\]]+)\]/);
          if (m && m[1] && typeof value === 'string') {
            const val = value.trim();
            if (val !== '') properties[m[1]] = val;
          }
        }
      } catch (e) { /* ignore */ }

      const searchContainers = [form];
      const parent = form.closest('section, [data-section-id], .product-single, .product');
      if (parent && parent !== form) searchContainers.push(parent);

      searchContainers.forEach((container) => {
        container.querySelectorAll(
          'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
        ).forEach((input) => {
          if (input.disabled) return;
          if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
          const m = input.name.match(/^properties\[([^\]]+)\]/);
          if (m && m[1]) {
            const val = (input.value || '').trim();
            if (val !== '') properties[m[1]] = val;
          }
        });
      });

      const baseItem = { id: parseInt(baseVariantId, 10), quantity };
      if (Object.keys(properties).length > 0) baseItem.properties = properties;

      const items = [];
      this._selected.forEach((charm) => {
        const vid = parseInt(charm.variantId, 10);
        if (!isNaN(vid)) items.push({ id: vid, quantity: 1 });
      });
      items.push(baseItem);

      // Try Rebuy SDK first
      if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.add === 'function') {
        try {
          await window.Rebuy.Cart.add({ items });
          setTimeout(async () => {
            try {
              const res      = await fetch('/cart.js');
              const cartData = await res.json();
              document.dispatchEvent(new CustomEvent('cart:updated',     { bubbles: true, detail: { cart: cartData } }));
              document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: cartData, cart: cartData } }));
              if (window.CartJS) CartJS.getCart();
            } catch (e) {}
          }, 50);
          return;
        } catch (e) {
          console.warn('[CharmBuilder] Rebuy SDK add failed, using fetch:', e);
        }
      }

      try {
        const response = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ items }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || 'Cart add failed');
        }

        const data = await response.json();

        if (window.Rebuy) {
          if (window.Rebuy.Cart && typeof window.Rebuy.Cart.getCart === 'function') window.Rebuy.Cart.getCart(true);
          if (window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') window.Rebuy.SmartCart.show();
        }

        document.dispatchEvent(new CustomEvent('cart:updated',      { bubbles: true, detail: { cart: data } }));
        document.dispatchEvent(new CustomEvent('rebuy:cart.change', { bubbles: true, detail: { cart: data } }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data, cart: data } }));

        if (window.CartJS) CartJS.getCart();
        this._triggerCartDrawer(data);

      } catch (error) {
        console.error('[CharmBuilder] Add to cart error:', error);
        const errWrapper = form.closest('[data-section-id]')?.querySelector('.product-form__error-message-wrapper');
        const errMsg     = form.closest('[data-section-id]')?.querySelector('.product-form__error-message');
        if (errWrapper && errMsg) {
          errMsg.textContent = error.message || 'Could not add items to cart.';
          errWrapper.removeAttribute('hidden');
        }
      } finally {
        this._restoreSubmitBtn(submitBtn);
      }
    }

    _restoreSubmitBtn(btn) {
      if (!btn) return;
      btn.classList.remove('btn--loading');
      btn.removeAttribute('disabled');
    }

    _triggerCartDrawer(cartData) {
      if (window.CartDrawer && typeof window.CartDrawer.open === 'function') {
        window.CartDrawer.open(); return;
      }
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open(); return;
      }
      document.dispatchEvent(new CustomEvent('sidebar:open', { bubbles: true }));
      if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
        window.Rebuy.SmartCart.show();
      }
    }
  }

  /* --------------------------------------------------------------------------
     Required Field Validation Focus Helper
  -------------------------------------------------------------------------- */
  function focusFirstInvalidRequiredField(form) {
    if (!form) return false;

    const invalidInput = Array.from(
      form.querySelectorAll(
        '.bold_option_error input, .bold_option_error textarea, [required], [name^="properties["][name*="*"]'
      )
    ).find((input) =>
      !input.disabled &&
      input.type !== 'hidden' &&
      (input.closest('.bold_option_error') || (input.value || '').trim() === '')
    );

    if (invalidInput) {
      invalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const doFocus = () => {
        try { invalidInput.focus({ preventScroll: true }); } catch (e) { invalidInput.focus(); }
        if (typeof invalidInput.select === 'function' &&
            (invalidInput.type === 'text' || invalidInput.tagName === 'TEXTAREA')) {
          try { invalidInput.select(); } catch (e) {}
        }
      };
      doFocus();
      setTimeout(doFocus, 80);
      return true;
    }
    return false;
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add-to-cart], [type="submit"], [name="add"], .add-to-cart');
    if (!btn) return;
    const form = btn.closest('form') || document.querySelector('form[action*="/cart/add"]');
    setTimeout(() => focusFirstInvalidRequiredField(form), 10);
  }, true);

  /* --------------------------------------------------------------------------
     Initialise all Charm Builder instances
  -------------------------------------------------------------------------- */
  function initCharmBuilders() {
    document.querySelectorAll('[data-charm-builder]').forEach((el) => new CharmBuilder(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCharmBuilders);
  } else {
    initCharmBuilders();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('[data-charm-builder]')) initCharmBuilders();
  });

})();
